import {
  Component,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { IconComponent } from '../../atoms/icon/icon.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { LinkComponent } from '../../atoms/link/link.component';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import {
  CaasComponents,
  CaasFormField,
  CaasLink,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import LinkType from '../../../shared/enums/link-type.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { ModalType } from '../../../shared/enums/modal-type.enums';
import { Video } from '../../../shared/interfaces/video.interface';
import { Router } from '@angular/router';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-help-disruptor',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    ButtonComponent,
    LinkComponent,
    CapitalizePipe,
  ],
  templateUrl: './help-disruptor.component.html',
  styleUrl: './help-disruptor.component.scss',
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class HelpDisruptorComponent implements OnChanges {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() icon: string = 'alarm-clock';
  @Input() color: Colors = Colors.GREEN_LOGO;
  @Input() headline: string = 'default headline';
  @Input() content: string = 'default content';
  @Input() size: Sizes = Sizes.XXXLARGE;
  @Input() buttonLabel: string = 'Button Label';
  @Input() hasButton: boolean = false;
  @Input() layout: 'default' | 'fullgrid' | 'small' = 'default';
  @Input() buttonType: 'link' | 'button' = 'button';
  @Input() hrefType?: HrefType;
  @Input() href?: string;
  @Input() title?: string;
  @Input() video?: Video;

  // Public Variables for help-disruptor
  public ButtonType = ButtonType;
  public HrefType = HrefType;
  public ModalType = ModalType;
  public showModal = false;
  // Private Variable for buttonType
  private _buttonLayout: ButtonType | LinkType = ButtonType.TERTIARY;

  get buttonLayout(): ButtonType {
    return this._buttonLayout as ButtonType;
  }

  get linkLayout(): LinkType {
    return this._buttonLayout as LinkType;
  }

  set buttonLayout(value: ButtonType | LinkType) {
    this._buttonLayout = value;
  }

  constructor(
    private caasBodytextService: CaasBodytextService,
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData'] && this.caasData) {
      this.setCaasContent(this.caasData);
    }
  }

  handleButtonClick() {
    switch (this.hrefType) {
      case HrefType.ANGULAR_ROUTE:
        this.router.navigate(['/' + this.href]);
        this.document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case HrefType.EXTERNAL:
        window.open(this.href, '_blank');
        break;
      case HrefType.OPEN_MODAL_VIDEO:
        this.showModal = true;
        break;
      default:
        break;
    }
  }

  closeModal(event: boolean) {
    this.showModal = event;
  }

  private translateButtonType(link: CaasLink): ButtonType | LinkType {
    return this.caasBodytextService.checkButtonLayout(link);
  }

  private translateHrefType(link?: CaasLink): HrefType | undefined {
    if (link) {
      return this.caasBodytextService.getHrefType(link);
    }
    return undefined;
  }

  private translateHref(link?: CaasLink): string | undefined {
    if (link) {
      return this.caasBodytextService.getHref(link);
    }
    return undefined;
  }

  private translateVideo(link?: CaasLink): Video | undefined {
    if (link) {
      const { lt_link_video_type, lt_link_vimeo_id, lt_link_youtube_id } =
        link?.value?.formData || {};
      return this.caasBodytextService.getVideo(
        lt_link_video_type as CaasFormField,
        lt_link_vimeo_id as CaasSimpleField,
        lt_link_youtube_id as CaasSimpleField
      );
    }
    return undefined;
  }

  private setLayout(caasData: CaasComponents['value'][number]): void {
    const typeLabel =
      caasData.formData.st_type.value.label.toString().trim() ?? 'Normal';
    switch (typeLabel) {
      case 'Schmal':
        this.layout = 'small';
        break;
      case 'Volle Breite':
        this.layout = 'fullgrid';
        break;
      case 'Normal':
      default:
        this.layout = 'default';
        break;
    }
  }

  setCaasContent(caasData: CaasComponents['value'][number]): void {
    this.headline = caasData.formData.st_header?.value ?? '';
    this.content = this.caasBodytextService.extractTextEditorContent(
      caasData.formData.st_text?.value ?? ''
    );
    this.icon = caasData.formData.st_icon?.value?.toString() ?? '';
    this.hasButton =
      caasData.formData.st_link?.value?.formData?.lt_button_state?.value ===
      true;
    this.setLayout(caasData);
    if (this.hasButton) {
      this.buttonLabel =
        caasData.formData.st_link?.value?.formData?.lt_link_label?.value ?? '';
      this.buttonLayout = caasData.formData.st_link
        ? this.translateButtonType(caasData.formData.st_link)
        : ButtonType.TERTIARY;
      this.hrefType = this.translateHrefType(caasData.formData.st_link);
      this.href = this.translateHref(caasData.formData.st_link);
      this.video = this.translateVideo(caasData.formData.st_link);
      this.title =
        caasData?.formData?.st_link?.value?.formData?.lt_link_title?.value ??
        '';
    }
  }
}
